/**
 * ChatPage - Página principal do chat conversacional
 * @module features/chat/ChatPage
 *
 * Página que integra todos os componentes de chat com o AI-SDK useChat
 * para streaming SSE de respostas da IA.
 *
 * T002-FE: Interface de Chat Conversacional
 * T003-FE: Persistência de Sessão e Prevenção de Perda de Dados
 * T002-FE (US-007): Detecção de Fim do Assessment
 * T003-INT: Integração com Geração de Relatório
 */

import { Toaster } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useChat } from '@ai-sdk/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AssessmentCompletionOverlay } from './components/AssessmentCompletionOverlay';
import { ChatInput, type ChatInputHandle } from './components/ChatInput';
import { MessageList } from './components/MessageList';
import { useAssessmentCompletion } from './hooks/useAssessmentCompletion';
import { useBeforeUnload } from './hooks/useBeforeUnload';
import { useSessionRecovery } from './hooks/useSessionRecovery';
import { generateReport } from './services/report.service';
import {
  getAssessmentType,
  getHasHydrated,
  getSessionId,
  getUserData,
  useSessionStore,
} from './stores/sessionStore';
import type { ChatMessage } from './types/chat.types';

// =============================================================================
// CONSTANTS
// =============================================================================

const INITIAL_MESSAGE_CONTENT =
  'Olá! Estou pronto para iniciar o review. Por favor, me ajude a entender melhor o contexto da minha operação.';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Página principal do chat conversacional
 *
 * Features:
 * - Validação de sessão com redirect
 * - Integração AI-SDK useChat com streaming
 * - Primeira mensagem automática
 * - Estados visuais completos (loading, typing, error)
 * - Responsividade (mobile, tablet, desktop)
 * - Acessibilidade WCAG AA
 *
 * @example
 * ```tsx
 * <Route path="/assessment/chat" element={<ChatPage />} />
 * ```
 */
export function ChatPage() {
  const navigate = useNavigate();
  const messageListRef = useRef<HTMLDivElement>(null);
  // FIX-008: Ref para controlar foco do input
  const chatInputRef = useRef<ChatInputHandle>(null);
  // FIX-005: Ref para rastrear se já processamos esta montagem
  const hasProcessedMount = useRef(false);

  // Store selectors
  const userData = useSessionStore(getUserData);
  const sessionId = useSessionStore(getSessionId);
  const assessmentType = useSessionStore(getAssessmentType);
  const setMessages = useSessionStore((state) => state.setMessages);
  const hasHydrated = useSessionStore(getHasHydrated); // FIX-005

  // Session recovery hook (T003-FE)
  // FIX-005: Usar isLoading para aguardar verificação de recuperação completar
  const { isLoading: isRecoveryLoading, isExpired } = useSessionRecovery();

  // BeforeUnload hook (T003-FE)
  useBeforeUnload({ enabled: true });

  /**
   * Handler para geração de relatório em background (T003-INT)
   * Chamado quando o assessment é completado, ANTES da transição
   * Não bloqueia o fluxo - erros são apenas logados
   *
   * FIX-004: Captura mensagens diretamente do store state para garantir
   * que temos as mensagens mais recentes antes de qualquer limpeza
   */
  const handleAssessmentComplete = useCallback(() => {
    // FIX-004: Capturar mensagens diretamente do state atual do store
    // Isso garante que temos as mensagens mesmo se storedMessages estiver desatualizado
    const currentState = useSessionStore.getState();
    const currentMessages = currentState.session.messages;

    // Preparar dados para o relatório
    const reportData = {
      sessionId,
      assessmentType: assessmentType || 'tech',
      userData: {
        name: userData.name || '',
        email: userData.email || '',
        company: userData.company,
        role: userData.role,
      },
      messages: currentMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      })),
    };

    // Disparar geração em background (sem await)
    // Se endpoint não existir (404), será logado warning mas não quebra fluxo
    generateReport(reportData).catch((error) => {
      console.error('[ChatPage] Report generation failed:', error);
    });
  }, [sessionId, assessmentType, userData]);

  // Assessment completion detection hook (T002-FE US-007, T003-INT)
  const { isCompleted, isTransitioning, checkForCompletion, cleanContent } =
    useAssessmentCompletion({
      onComplete: handleAssessmentComplete,
    });

  // AI-SDK useChat hook - using type assertions to work around version mismatches
  const chatResult = useChat({
    api: `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/chat`,
    streamProtocol: 'text',
    body: {
      userData: {
        name: userData.name,
        email: userData.email,
        company: userData.company,
        role: userData.role,
      },
      assessmentType,
      sessionId,
    },
    onError: (err: Error) => {
      console.error('Chat error:', err);
      toast.error('Erro ao processar mensagem', {
        description: 'Por favor, tente novamente.',
      });
    },
    onFinish: (message: any) => {
      // Persistir mensagem do assistente ao finalizar (T003-FE)
      // FIX-005: Usar getState() para pegar mensagens atuais (evita closure stale)
      const currentMessages = useSessionStore.getState().session.messages;
      const chatMessage: ChatMessage = {
        id: message.id,
        role: 'assistant',
        content: message.content,
        createdAt: new Date(),
      };
      setMessages([...currentMessages, chatMessage]);

      // FIX-008: Restaurar foco no input apos streaming terminar
      setTimeout(() => {
        chatInputRef.current?.focus();
      }, 50);
    },
  });

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    error,
    append,
    setMessages: setAIMessages,
  } = chatResult;

  // REMOVIDO: Validacao de sessao que redirecionava se nao tiver dados
  // Agora a IA coleta os dados durante a conversa (Opcao B)

  // Redirect se sessao expirada (T003-FE)
  useEffect(() => {
    if (isExpired) {
      navigate('/assessment/selection');
    }
  }, [isExpired, navigate]);

  // FIX-005: Inicialização e restauração de sessão unificados
  // Este useEffect decide: restaurar mensagens existentes OU enviar mensagem inicial
  useEffect(() => {
    if (!hasHydrated) return;
    if (isRecoveryLoading) return;
    // REMOVIDO: if (!hasAllRequiredFields()) return; - agora IA coleta dados na conversa
    if (hasProcessedMount.current) return;

    hasProcessedMount.current = true;

    // Verificar se há mensagens no store (sessão com histórico)
    const currentStoredMessages = useSessionStore.getState().session.messages;

    if (currentStoredMessages.length > 0) {
      // CASO 1: Reload - restaurar mensagens existentes
      console.log('[ChatPage] Restaurando mensagens (reload):', currentStoredMessages.length);

      const aiMessages = currentStoredMessages.map((msg) => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
        createdAt: msg.createdAt,
      }));

      setAIMessages(aiMessages);

      // Scroll para ultima mensagem
      setTimeout(() => {
        messageListRef.current?.scrollTo({
          top: messageListRef.current.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    } else {
      // CASO 2: Sessão nova - enviar mensagem inicial
      console.log('[ChatPage] Enviando mensagem inicial (sessao nova)', sessionId);

      // Persistir mensagem inicial no store
      const initialMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: INITIAL_MESSAGE_CONTENT,
        createdAt: new Date(),
      };
      setMessages([initialMessage]);

      // Enviar mensagem inicial para o backend
      append({
        role: 'user',
        content: INITIAL_MESSAGE_CONTENT,
      });
    }
  }, [hasHydrated, isRecoveryLoading, sessionId, append, setMessages, setAIMessages]);

  // Check for assessment completion when messages change (T002-FE US-007)
  useEffect(() => {
    if (messages.length === 0) return;
    if (isCompleted) return;

    // Check the last assistant message for completion marker
    const lastMessage = messages[messages.length - 1];
    if (lastMessage && lastMessage.role === 'assistant') {
      checkForCompletion(lastMessage.content);
    }
  }, [messages, isCompleted, checkForCompletion]);

  // Converter mensagens do AI-SDK para nosso formato (clean markers for display)
  const chatMessages: ChatMessage[] = useMemo(
    () =>
      messages.map((msg: any) => ({
        id: msg.id,
        role: msg.role as 'user' | 'assistant',
        // Clean completion marker from displayed content (T002-FE US-007)
        content: msg.role === 'assistant' ? cleanContent(msg.content) : msg.content,
        createdAt: msg.createdAt || new Date(),
      })),
    [messages, cleanContent]
  );

  /**
   * Handler customizado de submit (T003-FE: persistir mensagem do usuario)
   */
  const handleChatSubmit = useCallback(() => {
    if (input.trim() && !isLoading) {
      // FIX-005: Usar getState() para pegar mensagens atuais (evita closure stale)
      const currentMessages = useSessionStore.getState().session.messages;

      // Persistir mensagem do usuario antes de enviar
      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: input.trim(),
        createdAt: new Date(),
      };
      setMessages([...currentMessages, userMessage]);

      handleSubmit(new Event('submit') as unknown as React.FormEvent);
    }
  }, [input, isLoading, handleSubmit, setMessages]);

  /**
   * Handler de mudanca no input
   */
  const handleInputValueChange = useCallback(
    (value: string) => {
      // Simular evento para handleInputChange do AI-SDK
      const syntheticEvent = {
        target: { value },
      } as React.ChangeEvent<HTMLTextAreaElement>;
      handleInputChange(syntheticEvent);
    },
    [handleInputChange]
  );

  // REMOVIDO: Early return que impedia renderizacao sem dados
  // Agora permite renderizar mesmo sem dados pre-coletados

  return (
    <main data-testid="chat-page" className="min-h-screen bg-background flex flex-col">
      <Toaster position="top-center" />

      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <h1 className="text-xl font-semibold text-foreground">
            Review de {assessmentType === 'tech' ? 'Performance Tech' : 'Governança'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {userData.name ? `Olá, ${userData.name}! ` : ''}Vamos começar seu diagnóstico.
          </p>
        </div>
      </header>

      {/* Chat Container */}
      <div
        data-testid="chat-container"
        aria-busy={isLoading}
        className={cn(
          'flex flex-col flex-1',
          'max-w-3xl w-full mx-auto px-4',
          'min-h-0' // Importante para flex shrink
        )}
      >
        {/* Error Message */}
        {error && (
          <div
            role="alert"
            className="m-4 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive"
          >
            <p className="font-medium">Ocorreu um erro</p>
            <p className="text-sm">{error.message || 'Por favor, tente novamente.'}</p>
          </div>
        )}

        {/* Message List */}
        <MessageList messages={chatMessages} isTyping={isLoading} />

        {/* Input Area */}
        <div className="sticky bottom-0 pb-4 pt-2 bg-gradient-to-t from-background via-background to-transparent">
          <ChatInput
            ref={chatInputRef}
            value={input}
            onChange={handleInputValueChange}
            onSubmit={handleChatSubmit}
            disabled={isLoading || isCompleted}
            placeholder={isCompleted ? 'Preparando seu relatório...' : 'Digite sua resposta...'}
          />
        </div>
      </div>

      {/* Completion Overlay (T002-FE US-007) */}
      <AssessmentCompletionOverlay isVisible={isTransitioning} />
    </main>
  );
}

export default ChatPage;
